import React, { useState, useEffect } from 'react';
import { getDisputes, getDisputesByEmail, Dispute, getAllDisputeRequests, getDisputeRequestsByEmail } from '../services/disputeService';
import { DisputeRequest } from '../types';
import { formatDisputeReason, getDisputeReasonInfo, isFinancialDispute, isSupportInquiry } from '../lib/disputeUtils';
import { formatCurrency } from '../lib/formatters';
import { DollarSign, HelpCircle, Filter } from 'lucide-react';

interface Props {
  tenantId: string;
  userEmail: string;
  isAdminOrManager: boolean;
}

export const DisputeHistory: React.FC<Props> = ({ tenantId, userEmail, isAdminOrManager }) => {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [disputeRequests, setDisputeRequests] = useState<DisputeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<'ALL' | 'FINANCIAL' | 'INQUIRY'>('ALL');

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) return;
      setLoading(true);
      try {
        let disputesData: Dispute[];
        let requestsData: DisputeRequest[];
        if (isAdminOrManager) {
          disputesData = await getDisputes(tenantId);
          requestsData = await getAllDisputeRequests(tenantId);
        } else {
          disputesData = await getDisputesByEmail(tenantId, userEmail);
          requestsData = await getDisputeRequestsByEmail(tenantId, userEmail);
        }
        setDisputes(disputesData);
        setDisputeRequests(requestsData);
      } catch (err) {
        console.error('Error fetching disputes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tenantId, userEmail, isAdminOrManager]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading history...</div>;
  
  const filteredDisputes = disputes.filter(d => {
    if (categoryFilter === 'FINANCIAL') return isFinancialDispute(d);
    if (categoryFilter === 'INQUIRY') return isSupportInquiry(d);
    return true;
  });

  const filteredRequests = disputeRequests.filter(r => {
    if (categoryFilter === 'FINANCIAL') return r.classification === 'ADJUSTMENT_ALLOCATION';
    if (categoryFilter === 'INQUIRY') return r.classification !== 'ADJUSTMENT_ALLOCATION';
    return true;
  });

  const pending = filteredDisputes.filter(d => d.status === 'Pending');
  const processed = filteredDisputes.filter(d => d.status === 'Resolved' || d.status === 'Rejected');
  const pendingRequests = filteredRequests.filter(d => d.status === 'PENDING_REVIEW');
  const processedRequests = filteredRequests.filter(d => d.status === 'RESOLVED' || d.status === 'REJECTED');

  return (
    <div className="card p-6 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 tracking-tight">Disputes & Inquiries History</h3>
          <p className="text-xs text-gray-400 font-medium mt-0.5">Audit log of resolved and pending submissions</p>
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-100 rounded-xl">
          <button
            onClick={() => setCategoryFilter('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              categoryFilter === 'ALL' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setCategoryFilter('FINANCIAL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
              categoryFilter === 'FINANCIAL' ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <DollarSign size={12} />
            Financial Tips
          </button>
          <button
            onClick={() => setCategoryFilter('INQUIRY')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
              categoryFilter === 'INQUIRY' ? 'bg-white text-indigo-600 shadow-xs' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <HelpCircle size={12} />
            Support Inquiries
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Submitted Disputes Section */}
        {(pending.length > 0 || pendingRequests.length > 0) && (
          <div className="mb-8">
            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Pending Submissions ({pending.length + pendingRequests.length})</h4>
            {pending.map(dispute => {
              const isFinancial = isFinancialDispute(dispute);
              return (
                <div key={dispute.id} className="p-4 border rounded-xl flex justify-between items-center bg-blue-50/60 border-blue-100 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-gray-900">{dispute.staffName}</p>
                      <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                        isFinancial ? 'bg-blue-100 text-blue-800' : 'bg-indigo-100 text-indigo-800'
                      }`}>
                        {isFinancial ? 'Financial Tip Dispute' : 'Support Inquiry'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {dispute.tipDate} | {dispute.tipType} {dispute.docNumber ? `| Doc#: ${dispute.docNumber}` : ''}
                    </p>
                    {(() => {
                      const empComment = dispute.employeeNotes || dispute.explanation || (dispute as any).description || (dispute as any).notes;
                      const mgrComment = dispute.managerNotes || (dispute as any).managerComment;
                      return (
                        <div className="space-y-1 mt-1">
                          {empComment && (
                            <p className="text-xs text-amber-900 bg-amber-50/80 p-1.5 rounded border border-amber-200/60">
                              <strong className="font-bold text-[10px] uppercase text-amber-800">Employee Note: </strong>
                              "{empComment}"
                            </p>
                          )}
                          {mgrComment && (
                            <p className="text-xs text-indigo-900 bg-indigo-50/80 p-1.5 rounded border border-indigo-200/60">
                              <strong className="font-bold text-[10px] uppercase text-indigo-800">Manager Note: </strong>
                              "{mgrComment}"
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="text-right">
                    {isFinancial ? (
                      <p className="font-black text-blue-600">
                        {formatCurrency(dispute.originalAmount || 0)} → {formatCurrency(dispute.correctedAmount || 0)}
                      </p>
                    ) : (
                      <p className="text-xs font-bold text-indigo-600">Inquiry</p>
                    )}
                    <p className="text-xs font-bold text-blue-600">Pending Review</p>
                  </div>
                </div>
              );
            })}
            {pendingRequests.map(req => (
              <div key={req.id} className="p-4 border rounded-xl flex justify-between items-center bg-blue-50/60 border-blue-100 mb-3">
                <div>
                  <p className="font-bold text-gray-900">{formatDisputeReason(req.classification)}</p>
                  <p className="text-sm text-gray-500">
                    Requested on: {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString() : 'N/A'}
                  </p>
                  {req.notes && (
                    <p className="text-xs text-gray-600 italic mt-1">"{req.notes}"</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-blue-600">Pending Review</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resolved/Rejected section */}
        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Processed Records ({processed.length + processedRequests.length})</h4>
        {processed.length === 0 && processedRequests.length === 0 && (
          <p className="text-gray-500 text-center py-8">No processed records found.</p>
        )}
        {processed.map(dispute => {
          const disputeDate = (dispute.createdAt?.toDate ? dispute.createdAt.toDate() : (dispute.createdAt?.seconds ? new Date(dispute.createdAt.seconds * 1000) : null))?.toLocaleDateString() || 'N/A';
          const reasonInfo = getDisputeReasonInfo(dispute.reason);
          const isFinancial = isFinancialDispute(dispute);
          return (
            <div key={dispute.id} className="p-4 border rounded-xl flex justify-between items-center bg-white border-gray-100 mb-3 hover:border-gray-200 transition-all">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-gray-900">{dispute.staffName}</p>
                  <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                    isFinancial ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700'
                  }`}>
                    {isFinancial ? 'Financial Tip Dispute' : 'Support Inquiry'}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {disputeDate} | {dispute.tipDate} | {dispute.tipType} {dispute.docNumber ? `| Doc#: ${dispute.docNumber}` : ''}
                </p>
                <div>
                  <span className="text-xs font-semibold text-gray-700">Reason: </span>
                  <span className="text-xs font-medium text-gray-900">{reasonInfo.label}</span>
                  {reasonInfo.description && (
                    <span className="text-xs text-gray-500 block">{reasonInfo.description}</span>
                  )}
                </div>
                {(() => {
                  const empComment = dispute.employeeNotes || dispute.explanation || (dispute as any).description || (dispute as any).notes;
                  const mgrComment = dispute.managerNotes || dispute.managerComment;
                  return (
                    <div className="space-y-1 mt-1.5">
                      {empComment && (
                        <p className="text-xs text-amber-900 bg-amber-50/80 p-2 rounded-lg border border-amber-200/60">
                          <strong className="font-bold text-[10px] uppercase text-amber-800">Employee Note: </strong>
                          "{empComment}"
                        </p>
                      )}
                      {mgrComment && (
                        <p className="text-xs text-indigo-900 bg-indigo-50/80 p-2 rounded-lg border border-indigo-200/60">
                          <strong className="font-bold text-[10px] uppercase text-indigo-800">Manager Note: </strong>
                          "{mgrComment}"
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="text-right shrink-0">
                {isFinancial && (
                  <p className="font-black text-gray-900">
                    {formatCurrency(dispute.originalAmount || 0)} → {formatCurrency(dispute.correctedAmount || 0)}
                  </p>
                )}
                <p className={`text-xs font-bold ${dispute.status === 'Resolved' ? 'text-green-600' : 'text-red-600'}`}>
                  {dispute.status}
                </p>
              </div>
            </div>
          );
        })}
        {processedRequests.map(req => (
          <div key={req.id} className="p-4 border rounded-xl flex justify-between items-center bg-white border-gray-100 mb-3">
            <div>
              <p className="font-bold text-gray-900">{formatDisputeReason(req.classification)}</p>
              <p className="text-sm text-gray-500">
                Processed on: {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString() : 'N/A'}
              </p>
              {req.notes && (
                <p className="text-xs text-gray-600 italic mt-1">"{req.notes}"</p>
              )}
            </div>
            <div className="text-right">
              <p className={`text-xs font-bold ${req.status === 'RESOLVED' ? 'text-green-600' : 'text-red-600'}`}>
                {req.status}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

